import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tablesAPI } from '../utils/api';

function DataTables() {
  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableLabels, setTableLabels] = useState({});
  const [editingTable, setEditingTable] = useState(null);
  const [editValue, setEditValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTables();
    loadTableLabels();
  }, []);

  const loadTableLabels = () => {
    const saved = localStorage.getItem('tableLabels');
    if (saved) {
      try {
        setTableLabels(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load table labels:', e);
      }
    }
  };

  const saveTableLabels = (labels) => {
    localStorage.setItem('tableLabels', JSON.stringify(labels));
    setTableLabels(labels);
  };

  const fetchTables = async () => {
    try {
      setIsLoading(true);
      const response = await tablesAPI.getTables();
      // Handle both old format (array of strings) and new format (array of objects)
      const tableData = response.data.map(item =>
        typeof item === 'string' ? { name: item, count: null } : item
      );
      setTables(tableData);
      setError('');
    } catch (err) {
      setError('Failed to fetch tables. Please check your database connection.');
      console.error('Error fetching tables:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableSelect = (tableName) => {
    navigate(`/data/table/${encodeURIComponent(tableName)}`);
  };

  const startEditing = (e, tableName) => {
    e.stopPropagation();
    setEditingTable(tableName);
    setEditValue(tableLabels[tableName] || '');
  };

  const saveLabel = (e, tableName) => {
    e.stopPropagation();
    const newLabels = { ...tableLabels };
    if (editValue.trim()) {
      newLabels[tableName] = editValue.trim();
    } else {
      delete newLabels[tableName];
    }
    saveTableLabels(newLabels);
    setEditingTable(null);
    setEditValue('');
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditingTable(null);
    setEditValue('');
  };

  const handleKeyPress = (e, tableName) => {
    if (e.key === 'Enter') {
      saveLabel(e, tableName);
    } else if (e.key === 'Escape') {
      cancelEdit(e);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Database Tables</h1>
        <p className="text-gray-600">Select a table to view and manage data. Click the edit icon to add custom labels.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="text-lg text-gray-600">Loading tables...</div>
        </div>
      ) : tables.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600">No tables found in the database</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tables.map((table) => (
            <div
              key={table.name}
              className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
              onClick={() => handleTableSelect(table.name)}
            >
              <div className="flex flex-col">
                {/* Custom Label Section */}
                <div className="mb-3 pb-3 border-b border-gray-200">
                  {editingTable === table.name ? (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyPress(e, table.name)}
                        placeholder="Enter custom label..."
                        className="flex-1 px-2 py-1 text-sm border border-primary-500 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        autoFocus
                      />
                      <button
                        onClick={(e) => saveLabel(e, table.name)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Save"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Cancel"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        {tableLabels[table.name] ? (
                          <p className="text-sm font-medium text-primary-600 truncate">
                            {tableLabels[table.name]}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Click edit to add label</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => startEditing(e, table.name)}
                        className="ml-2 p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Edit label"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {/* Table Name Section */}
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                      {table.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500">Click to manage</span>
                      {table.count !== null && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                          {table.count.toLocaleString()} {table.count === 1 ? 'row' : 'rows'}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    className="w-6 h-6 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Divider */}
      <hr className="my-8 border-gray-300" />

      {/* Queries & Views Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Queries & Views</h2>
        <p className="text-gray-600">Pre-built queries that show specific data from the database.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer border-l-4 border-orange-400"
          onClick={() => navigate('/data/query/damaged-stations')}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 truncate">Damaged Stations Data Query</h3>
              </div>
              <p className="text-sm text-gray-500 ml-7">Station photos & loop data from TCS_Maintenance_Repair_App</p>
            </div>
            <svg
              className="w-6 h-6 text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTables;
